import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Customer } from '@inyova/models';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import * as ProfileActions from '@profile/profile.actions';
import * as fromProfile from '@profile/profile.reducers';

import { emptySpacesValidator } from '@app/validators/empty-spaces-validator';
import { Country } from '@shared/models/Account';
import { PersonalDetails } from '@shared/models/Profile';

const POSTCODE_PATTERNS: Record<string, RegExp> = {
  CH: /^\d{4}$/,
  DE: /^\d{5}$/
};

@Component({
  selector: 'app-profile-address',
  templateUrl: './profile-address.component.html',
  styleUrls: ['./profile-address.component.scss'],
  standalone: false
})
export class ProfileAddressComponent implements OnInit {
  addressForm: FormGroup;
  addressUpdateLoading$: Observable<boolean>;
  countries$: Observable<Country[]>;

  @Input() personalDetails: Partial<PersonalDetails>;
  @Input() customer: Customer;
  @Output() discardChanges: EventEmitter<void> = new EventEmitter<void>();

  get cityControl(): AbstractControl | null {
    return this.addressForm.get('city');
  }

  get streetControl(): AbstractControl | null {
    return this.addressForm.get('street');
  }

  get numberControl(): AbstractControl | null {
    return this.addressForm.get('number');
  }

  get zipControl(): AbstractControl | null {
    return this.addressForm.get('zip');
  }

  get countryControl(): AbstractControl | null {
    return this.addressForm.get('country');
  }

  constructor(
    private fb: FormBuilder,
    private store: Store
  ) {}

  ngOnInit(): void {
    // init new form
    this.addressForm = this.initializeForm();

    this.updateForm(this.personalDetails);
    // get countries
    this.countries$ = this.store.select(fromProfile.selectCountries);
    this.store
      .select(fromProfile.selectCountries)
      .pipe(take(1))
      .subscribe((countries: Country[]) => {
        if (countries.length === 0) {
          this.store.dispatch(ProfileActions.fetchCountries());
        }
      });

    this.addressUpdateLoading$ = this.store.select(fromProfile.selectLoading);
  }

  initializeForm(): FormGroup {
    return this.fb.group({
      city: ['', [Validators.required, Validators.minLength(2), emptySpacesValidator]],
      street: ['', [Validators.required, Validators.minLength(2), emptySpacesValidator]],
      number: ['', [Validators.required, emptySpacesValidator]],
      zip: ['', [Validators.required, this.postcodeValidator()]],
      country: ['CH']
    });
  }

  updateForm(personalDetails: Partial<PersonalDetails>): void {
    this.addressForm.patchValue({
      ...personalDetails,
      country: personalDetails.country ?? this.customer?.app_location?.toUpperCase() ?? 'CH'
    });

    this.addressForm.updateValueAndValidity();
    this.zipControl?.updateValueAndValidity();
  }

  onAddressSubmit() {
    if (this.addressForm.invalid || this.cityControl.value === '') {
      return;
    }

    if (this.addressForm.pristine) {
      this.onDiscardChanges();
      return;
    }

    const personalDetails: Partial<PersonalDetails> = {
      street: this.streetControl.value,
      number: this.numberControl.value,
      zip: this.zipControl.value,
      city: this.cityControl.value
    };
    this.store.dispatch(ProfileActions.updateProfileAddress({ personalDetails }));
  }

  onDiscardChanges() {
    this.discardChanges.emit();
  }

  private postcodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '');
      if (!value) {
        return null;
      }

      const country = this.getAddressCountry(control);
      const pattern = country ? POSTCODE_PATTERNS[country] : null;

      return pattern && !pattern.test(value) ? { validZip: true } : null;
    };
  }

  private getAddressCountry(control: AbstractControl): string {
    const country =
      control.parent?.get('country')?.value ??
      this.personalDetails?.country ??
      this.customer?.app_location;

    return String(country ?? '').toUpperCase();
  }

}
